/**
 * The games you have starred, kept with your account.
 *
 * The storage, and why it is not an RPC and not localStorage, is in
 * `net/player-record.ts` - favourites and recently-played are the same shape
 * of thing and share it.
 *
 * Order is the order you starred them in. It is the only thing this list knows
 * that the catalogue does not, so nothing here sorts it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { readGameIds, writeGameIds } from './net/player-record.js';
import { useSession } from './session-context.js';

const KEY = 'favourites';

export interface Favourites {
  /** Game ids, in the order they were starred. */
  readonly ids: readonly string[];
  readonly has: (gameId: string) => boolean;
  readonly toggle: (gameId: string) => void;
  /** True until the first read comes back, so a heart does not flicker off. */
  readonly loading: boolean;
}

export function useFavourites(): Favourites {
  const { session } = useSession();
  const [ids, setIds] = useState<readonly string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session) {
      setIds([]);
      setLoading(false);
      return;
    }

    let live = true;
    void (async () => {
      const stored = await readGameIds(session, KEY);
      if (live) {
        setIds(stored);
        setLoading(false);
      }
    })();

    return () => {
      live = false;
    };
  }, [session]);

  /*
   * The screen changes first and the write follows.
   *
   * A heart that waits for a round trip feels broken on a slow connection,
   * and the cost of being wrong is that one star did not save - not that
   * something was lost. On failure it goes back to what the server has.
   */
  const latest = useRef(ids);
  latest.current = ids;

  const toggle = useCallback(
    (gameId: string) => {
      if (!session) return;

      const before = latest.current;
      const after = before.includes(gameId)
        ? before.filter((id) => id !== gameId)
        : [...before, gameId];

      setIds(after);
      void writeGameIds(session, KEY, after).catch(() => {
        setIds(before);
      });
    },
    [session],
  );

  const has = useCallback((gameId: string) => ids.includes(gameId), [ids]);

  return { ids, has, toggle, loading };
}
